import React from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';
import { AuthProvider } from './contexts/AuthContext';
import App from './App';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import NotAuthorized from './pages/public/NotAuthorized';
import EditPractitioner from './pages/practitioners/EditPractitioner';
import Practitioner from './pages/practitioners/Practitioner';
import AddClient from './pages/clients/AddClient';
import EditClient from './pages/clients/EditClient';
import Client from './pages/clients/Client';
import AddScan from './pages/clients/scans/AddScan';
import EditScan from './pages/clients/scans/EditScan';
import Scan from './pages/clients/scans/Scan';
import UploadScanFiles from './pages/clients/scans/UploadScanFiles';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import SignOut from "./components/SignOut";
import VideoLayout from './VideoLayout';
import PublicVideo from './pages/public/PublicVideo';
import PreScanForm from './pages/public/PreScanForm';
import VideoContext from './contexts/VideoContext';
import reportWebVitals from './reportWebVitals';
import ViewClientForm from './pages/clients/scans/ViewClientForm';
import OnboardingForm from './pages/practitioners/OnboardingForm';
import ViewOnboardingForm from './pages/practitioners/ViewOnboardingForm';
import Training from './pages/training/Training';
import ThankYou from './pages/public/ThankYou';
import PublicLayout from './PublicLayout';
import PublicFormLayout from './PublicFormLayout';
import Billing from './pages/practitioners/Billing';
import AdditionalContent from './pages/practitioners/AdditionalContent';
import EditPreScanForm from './pages/preScanForms/EditPreScanForm';
import PastScans from './pages/dashboards/PastScans';
import EditOnboardingForm from './pages/practitioners/EditOnboardingForm';
import FocusScans from './pages/practitioners/FocusScans';
import MiniScans from './pages/practitioners/MiniScans';
import Enroll from './pages/account/Enroll';
import SignIn from './pages/account/SignIn';
import EmailActionHandler from './pages/account/EmailActionHandler';
import WhatsNew from './pages/practitioners/WhatsNew';
import CostStructure from './pages/practitioners/CostStructure';
import Inbox from './pages/practitioners/Inbox';
import ScheduleCall from './pages/practitioners/ScheduleCall';
import EditEmotionsSummary from './pages/clients/scans/reports/EditEmotionsReport';
import Reports from './pages/admin/Reports';
import EditWellnessReport from './pages/clients/scans/reports/EditWellnessReport';
import RenuLayout from './RenuLayout';
import AppointmentDetails from './pages/renu/AppointmentDetails';
import EditClientReport from './pages/clients/scans/reports/EditClientReport';
import ViewSession from './pages/admin/ViewSession';
import EditSupplementReport from './pages/clients/scans/reports/EditSupplementReport';

// Define Routes
const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      { path: '/', element: <Home /> },
      {
        path: '/dashboard',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <Dashboard />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/prescan-forms/:preScanId',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <EditPreScanForm />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/past-scans',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <PastScans />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/add/:practitionerId',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <AddClient />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:id',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <Client />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:id/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <EditClient />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:id/scans/add',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <AddScan />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <EditScan />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/admin/reports',
        element: (
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <Reports />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/admin/reports/session/:sessionId',
        element: (
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <ViewSession />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/admin/reports/clientReport/:reportId/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <EditClientReport />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/admin/reports/emotions/:reportId/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <EditEmotionsSummary />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/admin/reports/wellness/:reportId/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <EditWellnessReport />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/admin/reports/supplements/:reportId/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin"]}>
            <EditSupplementReport />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <Scan />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId/editClientReport',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <EditClientReport />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId/editEmotionsReport',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <EditEmotionsSummary />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId/editWellnessReport',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <EditWellnessReport />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId/viewform',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <ViewClientForm />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/clients/:clientId/scans/:scanId/upload',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <UploadScanFiles />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <Practitioner />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <EditPractitioner />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/onboarding/add',
        element: (
          <RoleProtectedRoute allowedRoles={["practitioner"]}>
            <OnboardingForm />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/onboarding/edit',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <EditOnboardingForm />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/onboarding',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <ViewOnboardingForm />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/billing',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <Billing />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/focus-scans',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <FocusScans />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/mini-scans',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <MiniScans />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/content',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <AdditionalContent />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/whats-new',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <WhatsNew />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/cost-structure',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <CostStructure />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/content',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <AdditionalContent />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:id/schedule-call',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <ScheduleCall />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/practitioners/:practitionerId/inbox',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager", "practitioner"]}>
            <Inbox />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/training/:practitionerId',
        element: (
          <RoleProtectedRoute allowedRoles={["practitioner"]}>
            <Training />
          </RoleProtectedRoute>
        )
      },
      {
        path: '/training',
        element: (
          <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
            <Training />
          </RoleProtectedRoute>
        )
      },
      { path: '/login', element: <SignIn /> },
      { path: '/sign-in', element: <SignIn /> },
      { path: '/sign-out', element: <SignOut /> },
      { path: '/email-action', element: <EmailActionHandler /> },
      { path: '/create-account', element: <Enroll /> },
      { path: '/enroll', element: <Enroll /> },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
  {
    element: <PublicFormLayout />,
    children: [
      { path: '/public/client-forms', element: <PreScanForm /> },
    ],
  },
  {
    element: <PublicLayout />,
    children: [
      { path: '/public/thank-you', element: <ThankYou /> },
      { path: "/not-authorized", element: <NotAuthorized /> },
    ],
  },
  {
    element: <RenuLayout />,
    children: [
      { path: '/renu/appointment', element: <AppointmentDetails />},
    ],
  },
  {
    element: <VideoLayout />,
    children: [
      { path: '/videos/bioenergetic', element: <PublicVideo videoId={VideoContext.BioenergeticAnalysis} /> },
      { path: '/videos/imprint-remedy', element: <PublicVideo videoId={VideoContext.ImprintRemedy} /> },
      { path: '/videos/welcome', element: <PublicVideo videoId={VideoContext.Welcome} /> },
      { path: '/videos/demo', element: <PublicVideo videoId={VideoContext.DemoScan} /> },
      { path: '/videos/deep-dive', element: <PublicVideo videoId={VideoContext.WhatIsBioenergeticTesting} /> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <AuthProvider>
    <RouterProvider router={router} />
  </AuthProvider>
);

// Performance reporting
reportWebVitals();